"use client";

import { getImageProps } from "next/image";
import { normalizeSize } from "~lib/components/normalize-size";
import { cn } from "~utils";
import type { ImageItemProps } from "./types";

export function ImageItem({ blok }: Readonly<ImageItemProps>) {
  const {
    width,
    height,
    image,
    alt,
    objectFit = "contain",
    borderRadius,
    priority,
    objectPosition = "left",
    aspectRatio,
    imageMobile,
    imagePlacement,
  } = blok;

  const src = image?.filename ?? "";
  const srcMobile = imageMobile?.filename ?? "";

  // ALT
  const imageAlt = image?.alt ?? alt ?? "";

  // STYLES
  const containerStyles: React.CSSProperties = {
    ...(width ? { width: normalizeSize(width) } : { width: "100%" }),
    ...(height
      ? { height: normalizeSize(height) }
      : { aspectRatio: aspectRatio || "16 / 9" }),
  };

  // PLACEMENT
  const placement = imagePlacement || "justify-center items-center";

  // COMMON
  const common = {
    alt: imageAlt,
    sizes: "100vw",
    priority,
    style: {
      width: "100%",
      height: "100%",
    },
  };

  const desktopImage = image?.filename
    ? getImageProps({
        ...common,
        src,
        width: 0,
        height: 0,
      })
    : null;

  const mobileImage = imageMobile?.filename
    ? getImageProps({
        ...common,
        src: srcMobile,
        width: 0,
        height: 0,
      })
    : null;

  return (
    <section
      className={cn(
        "relative box-border overflow-hidden",
        borderRadius,
        placement,
      )}
      style={containerStyles}
    >
      {/* https://nextjs.org/docs/pages/api-reference/components/image#art-direction */}
      <picture>
        {desktopImage && (
          <source
            className={cn(objectFit, objectPosition)}
            media="(min-width: 40rem)"
            srcSet={desktopImage.props.srcSet}
          />
        )}
        {mobileImage && (
          <source
            className={cn(objectFit, objectPosition)}
            media="(max-width: 40rem)"
            srcSet={mobileImage.props.srcSet}
          />
        )}
        {/* biome-ignore lint/a11y/useAltText: <explanation> */}
        <img
          className={cn(objectFit, objectPosition)}
          {...(mobileImage ?? desktopImage)?.props}
        />
      </picture>
    </section>
  );
}
